import { forwardRef, Ref } from 'react'
import Link from 'next/link'
import { useRouter } from 'next/router'
import { useTranslation } from 'next-i18next'
import styled, { css } from 'styled-components'
import { vars } from '@machobear/design-system/css'

import ENV_VARS from '@/config'
import structure from '@/structure'
import { breakpoints, colors, zIndex } from '@/styles'
import { toUnit } from '@/utils/styles'
import { useCurrentLocation } from '@/hooks/use-current-location'
import useMediaQuery from '@/hooks/use-media-query'
import useSessionComplete from '@/hooks/use-session-complete'
import { AndroidButton, AppleButton } from '@/components/app-buttons'

import LanguageSelector from './language-selector'
import { ReactComponent as FacebookIcon } from './images/Facebook.svg'
import { ReactComponent as InstagramIcon } from './images/Instagram.svg'
import { ReactComponent as LivLogo } from './images/liv-rent-logo.svg'
import { ReactComponent as TwitterIcon } from './images/Twitter.svg'

const { DASHBOARD_URL } = ENV_VARS

const DetailsStyle = css`
  @media (max-width: 1099px) {
    margin-bottom: 76px;
  }

  @media (max-width: ${breakpoints.phoneMax}) {
    margin-bottom: 32px;
  }
`

const Wrapper = styled.footer<{
  minimal?: boolean
  listingDetailsPage?: boolean
}>`
  position: ${({ minimal }) => (minimal ? 'fixed' : 'relative')};
  left: 0px;
  right: 0px;
  bottom: 0px;
  z-index: ${zIndex.footer};
  height: auto;
  background-color: ${colors.white};
  margin-top: auto;
  ${({ listingDetailsPage }) => (listingDetailsPage ? DetailsStyle : {})}
`

const FooterContainer = styled.div`
  padding: 80px;
  background-color: #0e131b;
  z-index: 1;

  @media (max-width: 1200px) {
    padding: 60px 24px 80px;
  }

  @media (max-width: 835px) {
    padding: 40px 24px 80px;
  }
`

const InnerFooterContainer = styled.div`
  margin: 0 auto;
  max-width: 1240px;
  display: flex;
  flex-direction: column;
  gap: 48px;

  @media (max-width: 835px) {
    gap: 32px;
  }
`

const UpperRow = styled.div`
  padding-bottom: 40px;
  border-bottom: 1px solid ${vars.borderColor.neutralLight};
  display: flex;
  justify-content: space-between;
  gap: 24px;

  @media (max-width: 1200px) {
    flex-direction: column;
    gap: 48px;
  }

  @media (max-width: 835px) {
    padding-bottom: 32px;
  }

  @media (max-width: 700px) {
    gap: 32px;
  }
`

const LeftContainer = styled.div`
  display: flex;
  gap: 48px;

  @media (max-width: 835px) {
    flex-direction: column;
    gap: 40px;
  }
`

const LogoWrapper = styled.div``

const PrimaryLinks = styled.nav`
  display: flex;
  gap: 24px;

  @media (max-width: 1200px) {
    gap: 48px;
  }

  @media (max-width: 700px) {
    flex-direction: column;
    gap: 32px;
  }
`

const StackGroup = styled.div`
  display: flex;
  gap: 24px;

  @media (max-width: 1200px) {
    gap: 48px;
  }

  @media (max-width: 700px) {
    gap: 20px;
    padding-bottom: 32px;
    border-bottom: 1px solid ${vars.borderColor.neutralLight};
    justify-content: space-between;
  }
`

const LinkStack = styled.section`
  display: flex;
  flex-direction: column;
  gap: 20px;
  min-width: 120px;

  @media (max-width: 700px) {
    flex: 1;
  }
`

const StackHeading = styled.h2`
  font-family: 'Inter', sans-serif;
  font-size: 12px;
  line-height: 12px;
  font-weight: 700;
  color: ${colors.white};
  text-decoration: none;
  margin: 0;
  text-transform: uppercase;
`

const StyledLink = styled.a`
  font-family: 'Inter', sans-serif;
  font-size: 12px;
  line-height: 12px;
  font-weight: 400;
  color: ${colors.white};
  text-decoration: none;
  transition: color 0.1s ease-in-out;
  margin: 0;
  width: fit-content;

  &:hover {
    text-decoration: underline;
  }
`

const StyledButtonLink = styled.button`
  font-family: 'Inter', sans-serif;
  font-size: 12px;
  line-height: 12px;
  font-weight: 400;
  color: ${colors.white};
  text-decoration: none;
  transition: color 0.1s ease-in-out;
  margin: 0;
  padding: 0;
  width: fit-content;
  background: none;
  border: none;

  &:hover {
    text-decoration: underline;
  }
`

const LivLogoLink = styled.a`
  svg {
    flex-shrink: 0;
  }
`

const DownloadLinks = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;

  @media (max-width: 1200px) {
    flex-direction: row;
  }

  @media (max-width: 700px) {
    flex-direction: column;
  }
`

const RightContainer = styled.div`
  display: flex;
  gap: 48px;

  @media (max-width: 1200px) {
    margin-left: 112px;
  }

  @media (max-width: 835px) {
    margin-left: 0;
  }

  @media (max-width: 700px) {
    gap: 20px;
    justify-content: space-between;
  }
`

const SocialLinks = styled.div`
  display: flex;
  gap: 24px;
`

const IconLink = styled.a`
  opacity: 1;
  transition: opacity 150ms ease;
  flex-shrink: 0;

  &:hover {
    opacity: 0.5;
  }
`

const LowerRow = styled.div<{
  minimal?: boolean
}>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 48px;

  ${props =>
    props.minimal && `height: ${toUnit(structure.footer.minimal.height)};`}

  @media (max-width: 835px) {
    flex-direction: column;
    gap: 32px;
  }

  @media (max-width: 700px) {
    align-items: flex-start;
  }
`

const Lower = styled.div`
  display: flex;
  flex-direction: column;
  gap: 32px;
`

const MlsText = styled.p`
  font-family: 'Inter', sans-serif;
  color: #eaecf1;
  font-size: 12px;
  line-height: 18px;
  font-weight: 400;
  margin: 0;
  max-width: 450px;

  @media (max-width: 835px) {
    text-align: center;
  }

  @media (max-width: 700px) {
    text-align: left;
  }
`

const CopyrightText = styled.p`
  font-family: 'Inter', sans-serif;
  color: #eaecf1;
  font-size: 12px;
  line-height: 18px;
  font-weight: 400;
  margin: 0;

  @media (max-width: 835px) {
    text-align: center;
  }

  @media (max-width: 700px) {
    text-align: left;
  }
`

const LowerRightContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 24px;

  @media (max-width: 700px) {
    justify-content: space-between;
    width: 100%;
  }
`

const TermsLinks = styled.div`
  display: flex;
  align-items: center;
  span {
    height: 20px;
    border-right: 1px solid #fff;
    padding-right: 12px;
    margin-right: 12px;
  }
`

// Minimal footer (used on some utility pages like reset password)
const MinimalContainer = styled.div`
  padding: 0 80px;
  background-color: #0e131b;
  z-index: 1;

  @media (max-width: ${breakpoints.desktop}) {
    padding: 0 24px;
  }

  @media (max-width: 700px) {
    padding: 0 16px;
  }
`

const MinimalRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 32px;
  height: ${toUnit(structure.footer.minimal.height)};
`

const currentYear = new Date().getFullYear()

type Props = {
  listingDetailsPage?: boolean
  hideOnBreakpoint?: boolean
  className?: string
}

const FooterComponent = forwardRef(
  (
    { listingDetailsPage, className, hideOnBreakpoint, ...rest }: Props,
    ref: Ref<HTMLElement>
  ) => {
    const { t } = useTranslation()
    const isLoggedIn = useSessionComplete()
    const router = useRouter()
    const { getCurrentLocation } = useCurrentLocation()
    const isChinese = router.locale === 'zh'
    const blogUrl = isChinese
      ? 'https://liv.rent/blog/zh/'
      : 'https://liv.rent/blog/'
    const isHidden = useMediaQuery(`(max-width: ${hideOnBreakpoint})`)
    if (isHidden) return null

    const onNearMeClick = async (filterSlug: string) => {
      const response = await getCurrentLocation()
      const filter = filterSlug ? `/${filterSlug}` : ''
      if (response) {
        router.push(`/rental-listings/city/${response.slug}${filter}`)
      } else {
        router.push(`/rental-listings${filter}`)
      }
    }

    return (
      <Wrapper
        role="contentinfo"
        listingDetailsPage={listingDetailsPage}
        className={className}
        ref={ref}
        {...rest}
      >
        <FooterContainer>
          <InnerFooterContainer>
            <UpperRow>
              <LeftContainer>
                <LogoWrapper>
                  <Link href="/" passHref legacyBehavior>
                    <LivLogoLink
                      aria-label={t(
                        'b.landing.home.label',
                        'liv.rent Homepage'
                      )}
                    >
                      <LivLogo height={30} width={64} />
                    </LivLogoLink>
                  </Link>
                </LogoWrapper>
                <PrimaryLinks>
                  <StackGroup>
                    <LinkStack>
                      <StackHeading>{t('Company')}</StackHeading>
                      <Link href="/about" passHref legacyBehavior>
                        <StyledLink>{t('About')}</StyledLink>
                      </Link>
                      <StyledLink href={blogUrl}>
                        {t('b.footer.view.blog.link', 'Blog')}
                      </StyledLink>
                      <Link href="/faq" passHref legacyBehavior>
                        <StyledLink>{t('FAQ')}</StyledLink>
                      </Link>
                      <StyledLink
                        href="https://angel.co/liv-rent/jobs"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {t('b.footer.view.careers.link', 'Careers')}
                      </StyledLink>
                      <StyledLink
                        href={
                          isLoggedIn
                            ? `${DASHBOARD_URL}/chat/support-room`
                            : 'mailto:info@liv.rent'
                        }
                      >
                        {t('Contact Us')}
                      </StyledLink>
                    </LinkStack>
                    <LinkStack>
                      <StackHeading>{t('Landlords')}</StackHeading>
                      <Link href="/post-a-listing" passHref legacyBehavior>
                        <StyledLink>{t('Post a Listing')}</StyledLink>
                      </Link>
                      <StyledLink
                        href="https://calendly.com/livrent/30min-educational-session"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {t('Book a Demo')}
                      </StyledLink>
                      <StyledLink href="https://landlords.liv.rent/screening/">
                        {t('Tenant Screening')}
                      </StyledLink>
                      <StyledLink href="https://verify.liv.rent">
                        {t(
                          'b.footer.view.verify_contract.link',
                          'Verify Contract'
                        )}
                      </StyledLink>
                      <StyledLink
                        href="https://liv.rent/blog/landlords/"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {t('Landlord Resources')}
                      </StyledLink>
                    </LinkStack>
                  </StackGroup>
                  <StackGroup>
                    <LinkStack>
                      <StackHeading>{t('Renters')}</StackHeading>
                      <Link href="/rental-listings" passHref legacyBehavior>
                        <StyledLink>{t('Browse Listings')}</StyledLink>
                      </Link>
                      <StyledLink
                        href="https://liv.rent/blog/rent-reports/"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {t('Rent Reports')}
                      </StyledLink>
                      <StyledLink
                        href="https://liv.rent/blog/renters/"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {t('Renter Resources')}
                      </StyledLink>
                    </LinkStack>
                    <LinkStack>
                      <StackHeading>{t('Popular Near Me')}</StackHeading>
                      <StyledButtonLink
                        type="button"
                        role="link"
                        onClick={() => onNearMeClick('apartments')}
                      >
                        {t('Apartments Near Me')}
                      </StyledButtonLink>
                      <StyledButtonLink
                        type="button"
                        role="link"
                        onClick={() => onNearMeClick('apartments')}
                      >
                        {t('Condos Near Me')}
                      </StyledButtonLink>
                      <StyledButtonLink
                        type="button"
                        role="link"
                        onClick={() => onNearMeClick('houses')}
                      >
                        {t('Houses Near Me')}
                      </StyledButtonLink>
                      <StyledButtonLink
                        type="button"
                        role="link"
                        onClick={() => onNearMeClick('rooms')}
                      >
                        {t('Rooms Near Me')}
                      </StyledButtonLink>
                      <StyledButtonLink
                        type="button"
                        role="link"
                        onClick={() => onNearMeClick('')}
                      >
                        {t('Rentals Near Me')}
                      </StyledButtonLink>
                    </LinkStack>
                  </StackGroup>
                </PrimaryLinks>
              </LeftContainer>
              <RightContainer>
                <LinkStack>
                  <StackHeading>{t('Follow Us')}</StackHeading>
                  <SocialLinks>
                    <IconLink
                      href="https://x.com/liv_rent"
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label="Twitter"
                    >
                      <TwitterIcon height={16} width={16} />
                    </IconLink>
                    <IconLink
                      href="https://www.instagram.com/liv.rent/"
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label="Instagram"
                    >
                      <InstagramIcon height={16} width={16} />
                    </IconLink>
                    <IconLink
                      href="https://www.facebook.com/liv.rent/"
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label="Facebook"
                    >
                      <FacebookIcon height={16} width={16} />
                    </IconLink>
                  </SocialLinks>
                </LinkStack>
                <LinkStack>
                  <StackHeading>{t('Download Now')}</StackHeading>
                  <DownloadLinks>
                    <a
                      href="https://apps.apple.com/ca/app/liv-apartment-houses-rental/id1321741040"
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label="Download liv.rent iOS App"
                    >
                      <AppleButton height={45} />
                    </a>
                    <a
                      href="https://play.google.com/store/apps/details?id=rent.liv.ether"
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label="Download liv.rent Android App"
                    >
                      <AndroidButton height={45} />
                    </a>
                  </DownloadLinks>
                </LinkStack>
              </RightContainer>
            </UpperRow>
            <Lower>
              <CopyrightText>
                Liv Strategies Inc. © {currentYear} | Vancouver, BC |{' '}
                {t('Apartments & Homes for Rent')}
              </CopyrightText>
              <LowerRow>
                <MlsText>
                  MLS® data provided by the Toronto Regional Real Estate Board
                  (TRREB) and is displayed under agreement with Prompton Real
                  Estate Services Corp.
                </MlsText>
                <LowerRightContainer>
                  <TermsLinks>
                    <Link href="/privacy" passHref legacyBehavior>
                      <StyledLink>{t('Privacy')}</StyledLink>
                    </Link>
                    <span></span>
                    <Link href="/terms" passHref legacyBehavior>
                      <StyledLink>{t('Terms')}</StyledLink>
                    </Link>
                  </TermsLinks>
                  <LanguageSelector clickId="footer" />
                </LowerRightContainer>
              </LowerRow>
            </Lower>
          </InnerFooterContainer>
        </FooterContainer>
      </Wrapper>
    )
  }
)

FooterComponent.displayName = 'FooterComponent'

export type FooterProps = {
  visible?: boolean
  listingDetailsPage?: boolean
  hideOnBreakpoint?: boolean
  theme?: string
  className?: string
}

const Footer: React.FC<FooterProps> = forwardRef(
  (
    { visible = true, listingDetailsPage = false, className, theme, ...rest },
    ref: Ref<HTMLElement>
  ) => {
    if (!visible) return null

    if (theme === 'minimal')
      return (
        <Wrapper
          listingDetailsPage={listingDetailsPage}
          minimal
          className={className}
          {...rest}
        >
          <MinimalContainer>
            <InnerFooterContainer>
              <MinimalRow>
                <CopyrightText>{`Liv Strategies Inc. © ${currentYear}`}</CopyrightText>
                <LanguageSelector clickId="footer" />
              </MinimalRow>
            </InnerFooterContainer>
          </MinimalContainer>
        </Wrapper>
      )

    return (
      <FooterComponent
        listingDetailsPage={listingDetailsPage}
        className={className}
        ref={ref}
        {...rest}
      />
    )
  }
)

Footer.displayName = 'Footer'

export default Footer
